/**
 * Cross-statement dedupe: the same payee seen in three months of statements is ONE
 * obligation, not three.
 *
 * 🔴 G7 — this is not a new rule. It is the rule the `obligations-report` route has been
 * applying inline since the scanner shipped, lifted into `shared/` unchanged (s217, Lane 3)
 * because the planner-side batch view is now a second caller. A second hand-typed copy of
 * a dedupe rule is how two surfaces come to disagree about how many bills you have.
 *
 * The key is `normalizePayee` (`shared/scanner-pads-target.ts`) — case, spaces AND
 * punctuation — the SAME rule `bulk-add` uses to decide whether a scanned payee is already in
 * the plan (D-217.5, s218). Until s218 this used `toLowerCase().trim()`, so "Hydro-One" and
 * "Hydro One" were one row when added to the plan but two lines in the obligations report.
 * The winner of a duplicate set is the most recently created row.
 */
import { isSamePayee, normalizePayee } from "./scanner-pads-target";

export interface DedupableObligation {
  cleanName: string;
  createdAt: Date | string;
}

export function dedupeObligationsByPayee<T extends DedupableObligation>(rows: readonly T[]): T[] {
  // s220 Lane E — PAYEE-RENAME-MERGE-1 (D-219.12). The key is still `normalizePayee` and it
  // is tried FIRST; `isSamePayee` adds only the ruled loosening, so the model renaming a
  // payee between scans ("TD Mortgage Payment" → "TD Mortgage") is one line here for the
  // same reason it is one row in the plan. Both callers moved together — one rule, one home.
  //
  // 🔴 Never guess: a row matching MORE THAN ONE kept row joins none of them by fuzz.
  const kept: T[] = [];
  for (const t of rows) {
    const key = normalizePayee(t.cleanName);
    let at = kept.findIndex(k => normalizePayee(k.cleanName) === key);
    if (at === -1) {
      const fuzzy = kept.reduce<number[]>((acc, k, i) => (isSamePayee(k.cleanName, t.cleanName) ? [...acc, i] : acc), []);
      if (fuzzy.length === 1) at = fuzzy[0];
    }
    if (at === -1) kept.push(t);
    else if (new Date(t.createdAt) > new Date(kept[at].createdAt)) kept[at] = t;
  }
  return kept;
}
